import React, { useState, useEffect } from 'react'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { LoadingSpinner } from './LoadingSpinner'
import { DuplicateShiftModalMetaProps, Event, Area } from '../data/types'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { sortByName } from '../utils/sorting'

type Props = {
    meta: DuplicateShiftModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (options: DuplicateShiftOptions) => Promise<void>
    shiftTitle: string
    currentAreaId: number
    currentEventId: number
}

export type DuplicateShiftOptions = {
    count: number
    includeAllocations: boolean
    targetAreaId: number | null // null means same area
}

export const DuplicateShiftModal = (props: Props) => {
    const { meta, isOpen, onClose, onSubmit, shiftTitle, currentAreaId, currentEventId } = props

    const [count, setCount] = useState<number>(1)
    const [includeAllocations, setIncludeAllocations] = useState(false)
    const [duplicateTarget, setDuplicateTarget] = useState<'same' | 'different'>('same')
    const [loading, setLoading] = useState(false)
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // For selecting different area
    const [allEvents, setAllEvents] = useState<Event[]>([])
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)

    // Get the selected event from state
    const selectedEvent = allEvents.find(e => e.id === selectedEventId)

    // Fetch events when modal opens and user wants different area
    useEffect(() => {
        const fetchEvents = async () => {
            if (!isOpen || duplicateTarget !== 'different') return
            if (allEvents.length > 0) return // Already loaded

            setLoadingEvents(true)
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (!response.ok) throw new Error('Failed to load events')
                const data = await response.json()
                setAllEvents(data)
            } catch {
                setError('Failed to load events')
            } finally {
                setLoadingEvents(false)
            }
        }

        fetchEvents()
    }, [isOpen, duplicateTarget, allEvents.length])

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCount(1)
            setIncludeAllocations(false)
            setDuplicateTarget('same')
            setSelectedEventId(null)
            setSelectedAreaId(null)
            setError(null)
        }
    }, [isOpen])

    // Reset area when event changes
    useEffect(() => {
        setSelectedAreaId(null)
    }, [selectedEventId])

    const handleEventChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const eventId = Number(e.target.value) || null
        setSelectedEventId(eventId)
    }

    const handleAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const areaId = Number(e.target.value) || null
        setSelectedAreaId(areaId)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (count < 1 || count > 50) {
            setError(meta.countValidationError)
            return
        }

        if (duplicateTarget === 'different' && !selectedAreaId) {
            setError(meta.selectAreaError)
            return
        }

        setLoading(true)
        setError(null)

        try {
            await onSubmit({
                count,
                includeAllocations,
                targetAreaId: duplicateTarget === 'different' ? selectedAreaId : null
            })
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={meta.title}
            maxWidth="md"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        onClick={onClose}
                        variant="outline"
                        disabled={loading}
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || (duplicateTarget === 'different' && !selectedAreaId)}
                        color="primary"
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form onSubmit={handleSubmit}>
                <div className="px-6 py-4">
                    {error && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {meta.description}
                    </p>

                    {/* Shift being duplicated */}
                    <div className="mb-4 bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {shiftTitle}
                        </p>
                    </div>

                    {/* Number of copies */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {meta.countLabel}
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={count}
                            onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                            placeholder={meta.countPlaceholder}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                     focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Include allocations checkbox */}
                    <div className="mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeAllocations}
                                onChange={(e) => setIncludeAllocations(e.target.checked)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded
                                         focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                {meta.includeAllocationsLabel}
                            </span>
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ms-6">
                            {meta.includeAllocationsDescription}
                        </p>
                    </div>

                    {/* Duplicate target selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {meta.destinationLabel}
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="duplicateTarget"
                                    value="same"
                                    checked={duplicateTarget === 'same'}
                                    onChange={() => setDuplicateTarget('same')}
                                    className="w-4 h-4 text-primary-600 border-gray-300
                                             focus:ring-primary-500 dark:border-gray-600"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {meta.sameAreaLabel}
                                </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="duplicateTarget"
                                    value="different"
                                    checked={duplicateTarget === 'different'}
                                    onChange={() => setDuplicateTarget('different')}
                                    className="w-4 h-4 text-primary-600 border-gray-300
                                             focus:ring-primary-500 dark:border-gray-600"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {meta.differentAreaLabel}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Event/Area selectors (shown when different area is selected) */}
                    {duplicateTarget === 'different' && (
                        <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            {loadingEvents ? (
                                <LoadingSpinner meta={LoadingSpinnerMeta} />
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.eventLabel}
                                        </label>
                                        <select
                                            value={selectedEventId ?? ''}
                                            onChange={handleEventChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                                     focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        >
                                            <option value="">{meta.selectEventPlaceholder}</option>
                                            {sortByName(allEvents).map(event => (
                                                <option key={event.id} value={event.id}>
                                                    {event.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedEvent && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {meta.areaLabel}
                                            </label>
                                            <select
                                                value={selectedAreaId ?? ''}
                                                onChange={handleAreaChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                                         focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            >
                                                <option value="">{meta.selectAreaPlaceholder}</option>
                                                {sortByName(selectedEvent.areas).map(area => (
                                                    <option key={area.id} value={area.id}>
                                                        {area.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </form>
        </Modal>
    )
}
